import AccountService from "../services/AccountService.js";
import AccountSync from "../services/AccountSync.js";
import * as PushReminders from "../services/PushReminders.js";

/**
 * Réglages du profil : rappels du Défi du jour (e-mail et notification),
 * nouveautés par e-mail et suppression du compte en libre service (RGPD).
 *
 * L'e-mail demande un compte ; la notification se règle par appareil, avec ou
 * sans compte. Les deux partagent l'heure choisie. Chaque changement part tout
 * de suite au serveur, sans bouton « Enregistrer » : un interrupteur décoché
 * doit couper les rappels sans étape de plus.
 */
export default class ProfileSettings {
    constructor() {
        this.page = document.querySelector(".page.profile");
        if (!this.page) return;
        this.t = (k) => window.geoNerdApp.languageManager.t(k);
        this.lang = window.geoNerdApp.languageManager.currentLang === "en" ? "en" : "fr";

        this.reminders = this.page.querySelector(".profile-reminders");
        this.emailRow = this.page.querySelector(".reminder-email");
        this.emailInput = this.page.querySelector(".reminder-email-input");
        this.emailPaused = this.page.querySelector(".reminder-email-paused");
        this.emailAnonymous = this.page.querySelector(".reminder-email-anonymous");
        this.pushRow = this.page.querySelector(".reminder-push");
        this.pushInput = this.page.querySelector(".reminder-push-input");
        this.pushNote = this.page.querySelector(".reminder-push-note");
        this.hourSelect = this.page.querySelector(".reminder-hour-select");
        this.message = this.page.querySelector(".reminders-message");

        this.newsSection = this.page.querySelector(".profile-news");
        this.newsInput = this.page.querySelector(".news-email-input");
        this.newsMessage = this.page.querySelector(".news-message");

        this.dataSection = this.page.querySelector(".profile-data");
        this.deleteForm = this.page.querySelector(".data-delete-form");
        this.dataMessage = this.page.querySelector(".data-message");

        this.fillHours();
        this.bind();
        (window.geoNerdApp.accountReady || Promise.resolve()).then(() => this.load());
    }

    fillHours() {
        if (!this.hourSelect) return;
        this.hourSelect.innerHTML = Array.from({ length: 24 }, (_, h) =>
            `<option value="${h}">${this.lang === "en" ? `${String(h).padStart(2, "0")}:00` : `${h} h`}</option>`
        ).join("");
        this.hourSelect.value = String(PushReminders.storedHour());
    }

    bind() {
        this.emailInput?.addEventListener("change", () => this.toggleEmail(this.emailInput.checked));
        this.newsInput?.addEventListener("change", () => this.toggleNews(this.newsInput.checked));
        this.pushInput?.addEventListener("change", () => this.togglePush(this.pushInput.checked));
        this.hourSelect?.addEventListener("change", () => this.changeHour(parseInt(this.hourSelect.value, 10)));
        this.page.querySelector(".data-delete")?.addEventListener("click", () => {
            this.deleteForm.hidden = false;
            this.deleteForm.querySelector("input")?.focus();
        });
        this.page.querySelector(".data-delete-cancel")?.addEventListener("click", () => {
            this.deleteForm.hidden = true;
        });
        this.deleteForm?.addEventListener("submit", (e) => this.deleteAccount(e));
    }

    async load() {
        const loggedIn = AccountSync.isLoggedIn();
        if (this.dataSection) this.dataSection.hidden = !loggedIn;
        if (this.newsSection) this.newsSection.hidden = !loggedIn;
        if (this.emailRow) this.emailRow.hidden = !loggedIn;
        if (this.emailAnonymous) this.emailAnonymous.hidden = loggedIn;

        if (loggedIn) {
            const { ok, data } = await AccountService.getPreferences();
            if (ok && data) this.applyPreferences(data);
        }
        await this.loadPush();
        // Sans compte ni notification possible, une heure de rappel ne réglerait rien.
        const hourRow = this.page.querySelector(".reminder-hour");
        if (hourRow) hourRow.hidden = !loggedIn && this.pushRow.hidden;
    }

    applyPreferences(prefs) {
        this.emailInput.checked = prefs.reminderEmail;
        if (this.newsInput && typeof prefs.newsEmail === "boolean") this.newsInput.checked = prefs.newsEmail;
        this.emailPaused.hidden = !prefs.reminderPaused;
        if (Number.isInteger(prefs.reminderHour)) this.hourSelect.value = String(prefs.reminderHour);
    }

    async loadPush() {
        if (!this.pushRow) return;
        if (!PushReminders.isSupported()) {
            // Sur iPhone, le push n'existe que pour l'application installée.
            this.pushRow.hidden = true;
            this.note(this.pushNote, /iphone|ipad|ipod/i.test(navigator.userAgent) ? this.t("reminders.pushIos") : "");
            return;
        }
        if (!(await PushReminders.serverKey())) {
            this.pushRow.hidden = true;
            return;
        }
        this.pushRow.hidden = false;
        this.pushInput.checked = Boolean(await PushReminders.currentSubscription());
        this.note(this.pushNote, PushReminders.permission() === "denied" ? this.t("reminders.pushBlocked") : "");
    }

    async toggleEmail(enabled) {
        const payload = { reminderEmail: enabled };
        if (enabled) Object.assign(payload, this.context());
        const { ok, data } = await AccountService.patchPreferences(payload);
        if (!ok) {
            this.emailInput.checked = !enabled;
            return this.show(this.t("reminders.error"), "error");
        }
        this.applyPreferences(data);
        this.show(this.t(enabled ? "reminders.emailOn" : "reminders.emailOff"), "success");
    }

    async toggleNews(enabled) {
        const { ok, data } = await AccountService.patchPreferences({ newsEmail: enabled });
        if (!ok) {
            this.newsInput.checked = !enabled;
            return this.show(this.t("reminders.error"), "error", this.newsMessage);
        }
        this.applyPreferences(data);
        this.show(this.t(enabled ? "newsEmail.on" : "newsEmail.off"), "success", this.newsMessage);
    }

    async togglePush(enabled) {
        if (!enabled) {
            await PushReminders.unsubscribe();
            return this.show(this.t("reminders.pushOff"), "success");
        }
        const result = await PushReminders.subscribe(parseInt(this.hourSelect.value, 10));
        if (result === "subscribed") return this.show(this.t("reminders.pushOn"), "success");
        this.pushInput.checked = false;
        this.show(this.t(result === "denied" ? "reminders.pushBlocked" : "reminders.error"), "error");
    }

    async changeHour(hour) {
        const tasks = [PushReminders.updateHour(hour)];
        if (AccountSync.isLoggedIn()) tasks.push(AccountService.patchPreferences({ reminderHour: hour, ...this.context() }));
        await Promise.all(tasks);
        this.show(this.t("reminders.hourSaved"), "success");
    }

    /** Langue et fuseau du joueur : le rappel est écrit dans sa langue et part à son heure. */
    context() {
        const ctx = { lang: this.lang };
        try {
            ctx.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) { /* le serveur prendra Paris */ }
        return ctx;
    }

    async deleteAccount(e) {
        e.preventDefault();
        const password = this.deleteForm.querySelector(".data-delete-password").value;
        if (!password) return;
        const res = await AccountService.deleteAccount(password);
        if (!res.ok) {
            const key = res.status === 403 ? "privacy.wrongPassword" : "reminders.error";
            return this.show(this.t(key), "error", this.dataMessage);
        }
        await PushReminders.unsubscribe().catch(() => {});
        AccountSync.clear();
        window.location.href = this.lang === "en" ? "/en/" : "/";
    }

    note(el, text) {
        if (!el) return;
        el.textContent = text;
        el.hidden = !text;
    }

    show(text, kind, el = this.message) {
        if (!el) return;
        el.textContent = text;
        const role = el === this.dataMessage ? "data-message" : el === this.newsMessage ? "news-message" : "reminders-message";
        el.className = `account-message ${kind} ${role}`;
    }
}
